/**
 * Shared color tokens for dashboard progress visuals.
 *
 *   emerald-500 — on track (≥75)
 *   amber-400   — at risk  (50–74)
 *   orange-400  — low      (<50)
 *
 * Callers that need a different tier scheme (different colors, labels, or
 * breakpoints) can define their own ProgressTier[] and pass it to the helpers
 * or to ScoreRing / ScoreCard via the `tiers` prop.
 */

export interface ProgressTier {
    /** Inclusive lower bound. The first matching tier wins. */
    min: number;
    /** Hex or rgba string — must be a real color string for recharts. */
    color: string;
    /** Human-readable label shown below the ring. */
    label: string;
}

export const PROGRESS_THRESHOLDS: readonly ProgressTier[] = [
    { min: 75, color: '#10b981', label: 'On track' },
    { min: 50, color: '#fbbf24', label: 'At risk' },
    { min: 0, color: '#fb923c', label: 'Low' },
];

/** Returns the tier (color + label) for a 0–100 progress value. */
export function getProgressTier(
    pct: number,
    tiers: readonly ProgressTier[] = PROGRESS_THRESHOLDS
): ProgressTier {
    return tiers.find((t) => pct >= t.min) ?? tiers[tiers.length - 1];
}

/** Returns just the color for a 0–100 progress value. */
export function getProgressColor(
    pct: number,
    tiers: readonly ProgressTier[] = PROGRESS_THRESHOLDS
): string {
    return getProgressTier(pct, tiers).color;
}
